'use client';

import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import config from 'config';
import { ShowSvcPriceProvider } from 'context/ShowSvcPriceContext';
import { useSearchParams } from 'next/navigation';
import { useQueryState } from 'nuqs';

import { Stack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  clearService,
  resetForm,
  setIsContinueBooking,
  setRedirectParams,
  setStep
} from 'store/reducers/bookingAppointmentSlice';
import { setError } from 'store/reducers/servicesSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import { BookingSteps, LocationFlowType, SearchParameters } from 'types';

import { ContinueShoppingModal } from 'components';

const {
  booking: { modalBanners, modalTitle, modalDescription, modalContinueBtnText }
} = config;

const BookingLayoutClient = ({ children }: { children: ReactNode }) => {
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [_, setServiceIds] = useQueryState(SearchParameters.Services);
  const { isLoading: isUserLoading } = useAppSelector((state) => state.user);
  const { isLoading: isLocationLoading } = useAppSelector(
    (state) => state.locations
  );
  const {
    isAddonsLoading,
    isBookingLoading,
    isLoading,
    isProvidersLoading,
    isServicesLoading
  } = useAppSelector((state) => state.services);
  const {
    isContinueBooking,
    selectedServices,
    moveApptId,
    isAuthRedirection,
    activeStep
  } = useAppSelector((state) => state.bookingAppointment);
  const [isDisplayModal, setIsDisplayModal] = useState(isContinueBooking);

  const loader =
    isAddonsLoading ||
    isProvidersLoading ||
    isBookingLoading ||
    isLoading ||
    isServicesLoading ||
    isLocationLoading ||
    isUserLoading;

  const handleCloseModal = () => {
    setIsDisplayModal(false);
    dispatch(setIsContinueBooking(false));
  };

  const handleReset = () => {
    dispatch(resetForm());
    setServiceIds(null);
    dispatch(clearService());
    dispatch(setIsContinueBooking(false));
    dispatch(setStep(BookingSteps.SERVICE));
    dispatch(setRedirectFlow(null));
  };

  const selectBookingFlow = useCallback(() => {
    if (activeStep > 1) {
      dispatch(setRedirectFlow(LocationFlowType.BOOK));
    }
    dispatch(setRedirectParams(searchParams.toString()));
  }, [dispatch, searchParams, activeStep]);

  useEffect(() => {
    selectBookingFlow();
  }, [selectBookingFlow]);

  useEffect(() => {
    dispatch(setIsContinueBooking(false));

    return () => {
      if (selectedServices.length && !isAuthRedirection) {
        dispatch(setIsContinueBooking(true));
      }
    };
  }, [selectedServices, dispatch, isAuthRedirection]);

  useLayoutEffect(() => {
    setError('');

    return () => {
      if (moveApptId) {
        handleReset();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ShowSvcPriceProvider>
      <Stack
        gap={0}
        justifyContent='center'
        w='100%'
        px={{ base: '15px', md: '25px' }}
        maxW='1330px'
      >
        {children}

        {!loader && (
          <ContinueShoppingModal
            isOpen={isDisplayModal}
            onClose={handleCloseModal}
            onContinueShopping={handleCloseModal}
            onResetShopping={handleReset}
            title={modalTitle}
            description={modalDescription}
            continueText={modalContinueBtnText}
            banners={modalBanners}
          />
        )}
      </Stack>
    </ShowSvcPriceProvider>
  );
};

export default BookingLayoutClient;
